// pipe: the personal navigation layout — PURE functions over the `nav.layout.v1` preference
// (`docs/superpowers/specs/2026-11-05-dashboard-and-nav-design.md` §4.2). The sidebar renders
// `applyNavLayout(DEFAULT_NAV_ORDER, saved).visible` and THEN runs every existing per-button gate
// unchanged: a preference never grants anything, it only orders and hides.

/** Stable ids of the ERP sidebar sections (the `navId` contract the preference stores). */
export type NavId =
  | 'dashboard'
  | 'all-docs'
  | 'records'
  | 'ops'
  | 'intake'
  | 'customers'
  | 'sites'
  | 'scan'
  | 'work'
  | 'director'
  | 'qms'
  | 'refining'
  | 'sales'
  | 'listings'
  | 'comms'
  | 'agreements'
  | 'inbound'
  | 'finance'
  | 'reports'
  | 'ask'
  | 'journal'
  | 'notifications';

/** The default order = Dashboard first, then today's sidebar order. */
export const DEFAULT_NAV_ORDER: readonly NavId[] = [
  'dashboard',
  'all-docs',
  'records',
  'ops',
  'intake',
  'customers',
  'sites',
  'scan',
  'work',
  'director',
  'qms',
  'refining',
  'sales',
  'listings',
  'comms',
  'agreements',
  'inbound',
  'finance',
  'reports',
  'ask',
  'journal',
  'notifications',
];

export interface NavLayout {
  order: NavId[];
  hidden: NavId[];
}

/** Resolve a saved layout against the current defaults: unknown ids are dropped, ids the layout
 *  does not mention are inserted where the defaults put them relative to their neighbours (so a
 *  section added in a later release shows up for users with an old saved order), and `hidden` is
 *  filtered to known ids. `visible` excludes hidden. */
export function applyNavLayout(
  defaults: readonly NavId[],
  layout: NavLayout | null
): { visible: NavId[]; hidden: NavId[] } {
  const known = new Set(defaults);
  const order = resolveOrder(defaults, layout?.order ?? []);
  const hidden = new Set((layout?.hidden ?? []).filter(id => known.has(id)));
  return {
    visible: order.filter(id => !hidden.has(id)),
    hidden: order.filter(id => hidden.has(id)),
  };
}

function resolveOrder(
  defaults: readonly NavId[],
  saved: readonly NavId[]
): NavId[] {
  const known = new Set(defaults);
  const seen = new Set<NavId>();
  const out: NavId[] = [];
  for (const id of saved) {
    if (known.has(id) && !seen.has(id)) {
      out.push(id);
      seen.add(id);
    }
  }
  // Insert each missing default BEFORE the first already-placed id that follows it in the
  // defaults (its default successor); at the end when none does.
  for (const [i, id] of defaults.entries()) {
    if (seen.has(id)) continue;
    const successors = defaults.slice(i + 1);
    const at = out.findIndex(placed => successors.includes(placed));
    if (at === -1) out.push(id);
    else out.splice(at, 0, id);
    seen.add(id);
  }
  return out;
}

function materialise(
  layout: NavLayout | null,
  defaults: readonly NavId[]
): NavLayout {
  const r = applyNavLayout(defaults, layout);
  return { order: resolveOrder(defaults, layout?.order ?? []), hidden: r.hidden };
}

/** Move `id` one step up (`-1`) or down (`+1`) in the full order; no-op at the ends. Pure. */
export function moveNav(
  layout: NavLayout | null,
  id: NavId,
  dir: -1 | 1,
  defaults: readonly NavId[]
): NavLayout {
  const next = materialise(layout, defaults);
  const order = [...next.order];
  const i = order.indexOf(id);
  const j = i + dir;
  if (i === -1 || j < 0 || j >= order.length) return next;
  [order[i], order[j]] = [order[j], order[i]];
  return { order, hidden: [...next.hidden] };
}

/** Hide a visible id or show a hidden one. Pure. */
export function toggleHidden(
  layout: NavLayout | null,
  id: NavId,
  defaults: readonly NavId[]
): NavLayout {
  const next = materialise(layout, defaults);
  const hidden = next.hidden.includes(id)
    ? next.hidden.filter(h => h !== id)
    : [...next.hidden, id];
  return { order: [...next.order], hidden };
}

/** Drag-and-drop: place `source` immediately before (`edge: 'top'`) or after (`'bottom'`)
 *  `target` in the full order. Unknown ids and a drop on itself change nothing. Pure. */
export function reorderNav(
  layout: NavLayout | null,
  source: NavId,
  target: NavId,
  edge: 'top' | 'bottom',
  defaults: readonly NavId[]
): NavLayout {
  const next = materialise(layout, defaults);
  const order = next.order.filter(id => id !== source);
  const at = order.indexOf(target);
  if (source === target || !next.order.includes(source) || at === -1) {
    return next;
  }
  order.splice(edge === 'top' ? at : at + 1, 0, source);
  return { order, hidden: [...next.hidden] };
}
